import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

/**
 * Attribution for the RMET, per the "RMET: crediti, copyright e attribuzioni"
 * spec (v1.0). The strings live in the `rmet_credits` locale namespace; what is
 * here is release configuration and the canonical URLs, which are never
 * translated.
 *
 * Do NOT add a standalone "© Autism Research Centre" or "Used with permission"
 * line anywhere: the ARC notes copyright may sit with the publisher. Attribute
 * the ARC as the source and the 2001 paper as the reference instead.
 */
const RMET_RIGHTS = {
  sourceUrl: 'https://www.autismresearchcentre.com/tests/eyes-test-adult/',
  paperUrl: 'https://doi.org/10.1111/1469-7610.00715',
  /**
   * Shows `rmet_credits.permission_notice`. Set to true only once the project
   * lead has checked its wording and scope against the permissions received.
   */
  permissionNoticeEnabled: false,
  /**
   * The exact legal notice required by the rights holders, once approved.
   * Shown verbatim (never translated) and takes precedence over the
   * permission notice above.
   */
  rightsHolderExactNotice: null as string | null,
};

/**
 * Renders the key when it has text. Some keys are deliberately empty in one
 * language — the Italian-translation credit has no English counterpart — so
 * an empty string means "omit the line", not "missing translation".
 */
const Line: React.FC<{ k: string; variant?: 'body2' | 'caption' }> = ({ k, variant = 'body2' }) => {
  const { t } = useTranslation();
  const text = t(`rmet_credits.${k}`);
  return text ? (
    <Typography variant={variant} color="text.secondary" display="block">
      {text}
    </Typography>
  ) : null;
};

/** Keyboard-accessible control that opens the credits panel. */
export const RmetCreditsLink: React.FC<{ onOpen: () => void }> = ({ onOpen }) => {
  const { t } = useTranslation();
  return (
    <Link component="button" type="button" variant="caption" onClick={onOpen}>
      {t('rmet_credits.credits_link')}
    </Link>
  );
};

/** Attribution block for the welcome screen. */
export const RmetIntroCredits: React.FC<{ onOpenCredits: () => void }> = ({ onOpenCredits }) => (
  <Box display="flex" flexDirection="column" gap={0.5}>
    {[
      'intro_original',
      'intro_translation',
      'intro_source',
      'intro_reference',
      'intro_non_diagnostic',
    ].map((k) => (
      <Line key={k} k={k} variant="caption" />
    ))}
    <Box>
      <RmetCreditsLink onOpen={onOpenCredits} />
    </Box>
  </Box>
);

/**
 * Short credit shown under every item — below the photo and the options, never
 * over them. It wraps onto a second line on narrow screens.
 */
export const RmetItemFooter: React.FC<{ onOpenCredits: () => void }> = ({ onOpenCredits }) => {
  const { t } = useTranslation();
  return (
    <Box
      component="footer"
      display="flex"
      flexWrap="wrap"
      columnGap={1}
      rowGap={0.25}
      alignItems="baseline"
      sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }}
    >
      <Typography variant="caption" color="text.secondary">
        {t('rmet_credits.item_footer')}
      </Typography>
      <RmetCreditsLink onOpen={onOpenCredits} />
    </Box>
  );
};

/** A source link that opens in a new tab and says so to assistive technology. */
const SourceLink: React.FC<{ labelKey: string; href: string }> = ({ labelKey, href }) => {
  const { t } = useTranslation();
  const label = t(`rmet_credits.${labelKey}`);
  return (
    <Typography variant="body2">
      {label}:{' '}
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${label}: ${href} (${t('rmet_credits.new_tab')})`}
        sx={{ wordBreak: 'break-all' }}
      >
        {href}
      </Link>
    </Typography>
  );
};

/**
 * The full "Credits and rights" panel, shared by the welcome, item and result
 * screens. A modal dialog, so it sits above any image and closes with Escape.
 */
export const RmetCreditsDialog: React.FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const notice =
    RMET_RIGHTS.rightsHolderExactNotice ??
    (RMET_RIGHTS.permissionNoticeEnabled ? t('rmet_credits.permission_notice') : null);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>{t('rmet_credits.credits_title')}</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={1.5}>
          {[
            'credits_original',
            'credits_translation',
            'credits_source',
            'credits_publication',
            'credits_non_diagnostic',
          ].map((k) => (
            <Line key={k} k={k} />
          ))}
          {notice && (
            <Typography variant="body2" color="text.secondary">
              {notice}
            </Typography>
          )}
          <Box>
            <SourceLink labelKey="source_link_label" href={RMET_RIGHTS.sourceUrl} />
            <SourceLink labelKey="doi_link_label" href={RMET_RIGHTS.paperUrl} />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};
